import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import ErrorEst from '~/components/ErrorEst/ErrorEst';
import ListSelect from '~/components/widgets/ListSelect/ListSelect';
import { useCurrentFrame } from '~/systems/currentFrame';
import { printQutritIndices, useUiState } from '~/systems/uiState';
import { assertNever } from '~/util';
import type { SimulatedComponent } from '~/systems/simulation';
import './QuantumState.css';

interface QuantumStateProps {
  headerNode: HTMLElement | null;
}

/** Probability threshold */
const MIN_PROBABILITY = 0.005;
/** Decimals - digits after 0. */
const DECIMALS = 3;

type ComplexRepr = 'cartesian' | 'polar' | 'polarPi' | 'polarTau';
type BasisRepr = 'computational' | 'physical';

const complexReprOptions = [
  { label: 'Polar [rad]', value: 'polar' as ComplexRepr },
  { label: 'Polar [π]', value: 'polarPi' as ComplexRepr },
  { label: 'Polar [τ]', value: 'polarTau' as ComplexRepr },
  { label: 'Cartesian', value: 'cartesian' as ComplexRepr }
];

const basisReprOptions = [
  { label: 'Computational', value: 'computational' as BasisRepr },
  { label: 'Physical', value: 'physical' as BasisRepr }
];

function arg(cx: { im: number; re: number }) {
  const a = Math.atan2(cx.im, cx.re);
  return a < 0 ? a + Math.PI * 2 : a;
}

function formatComplex(
  cx: { im: number; re: number; mag2: number },
  repr: ComplexRepr
): string {
  const rStr = Math.sqrt(cx.mag2).toFixed(DECIMALS);
  switch (repr) {
    case 'cartesian':
      return `${cx.re >= 0 ? '+' : '-'}${Math.abs(cx.re).toFixed(DECIMALS)} ${cx.im >= 0 ? '+' : '-'} ${Math.abs(cx.im).toFixed(DECIMALS)}i`;
    case 'polar':
      return `${rStr} e^${arg(cx).toFixed(DECIMALS)}i`;
    case 'polarPi':
      return `${rStr} e^${(arg(cx) / Math.PI).toFixed(DECIMALS)}πi`;
    case 'polarTau':
      return `${rStr} e^${(arg(cx) / (2 * Math.PI)).toFixed(DECIMALS)}τi`;
    default:
      return assertNever(repr);
  }
}

function pickStateNames(basis: BasisRepr): [string, string, string] {
  switch (basis) {
    case 'computational':
      return ['0', '1', 'r'];
    case 'physical':
      return ['g', 'h', 'r'];
    default:
      return assertNever(basis);
  }
}

export default function QuantumState({ headerNode }: QuantumStateProps) {
  const currentFrame = useCurrentFrame();

  const uiState = useUiState();
  const [complexRepr, setComplexRepr] = useState<ComplexRepr>('polar');

  const ketComponents = useMemo(() => {
    const stateNames = pickStateNames(uiState.basisRepr);
    const numAtoms = currentFrame.numAtoms;
    return currentFrame.components
      .filter((c) => c.mag2 > MIN_PROBABILITY)
      .sort((a, b) => b.mag2 - a.mag2)
      .map((c) => ({
        data: c,
        label: printQutritIndices(c.qutritIndices, numAtoms, stateNames)
      }));
  }, [currentFrame.components, currentFrame.numAtoms, uiState.basisRepr]);

  function componentHover(state: SimulatedComponent) {
    uiState.setHoveredState(state);
  }
  function componentBlur() {
    uiState.setHoveredState(null);
  }

  const hoverIndex =
    uiState.hoveredAtom == null
      ? null
      : currentFrame.numAtoms - uiState.hoveredAtom - 1;

  return (
    <>
      {headerNode && createPortal(<ErrorEst />, headerNode)}
      <div className="QuantumState u-scrollbarDark">
        <div className="QuantumState-stateContainer u-scrollbarDark">
          <div className="QuantumState-state">
            {ketComponents.map(({ data, label }, ketIndex) => (
              <div key={ketIndex} className="QuantumState-ketComponent">
                <div
                  className="QuantumState-component QuantumState-ketComponentInner"
                  onMouseEnter={() => componentHover(data)}
                  onMouseLeave={componentBlur}
                >
                  <div className="QuantumState-amplitude">
                    {formatComplex(data, complexRepr)}
                  </div>
                  <div className="QuantumState-qutrits">
                    <span>❘</span>
                    {label.split('').map((state, index) => (
                      <span
                        key={index}
                        className={`QuantumState-qutritState${index === hoverIndex ? ' is-active' : ''}`}
                      >
                        {state}
                      </span>
                    ))}
                    <span>⟩</span>
                  </div>
                </div>
                {ketComponents.length > 1 && <span>+</span>}
              </div>
            ))}
          </div>
        </div>
        <div className="QuantumState-legend">
          <div className="QuantumState-legendRow">
            <ListSelect
              value={complexRepr}
              onChange={(val) => setComplexRepr(val as ComplexRepr)}
              options={complexReprOptions}
            />
            <ListSelect
              value={uiState.basisRepr}
              onChange={(val) => uiState.setBasisRepr(val as BasisRepr)}
              options={basisReprOptions}
            />
          </div>
          <div className="QuantumState-legendRow">
            <div className="QuantumState-amplitude">Amplitude</div>
            <div className="QuantumState-qutrits">Qutrits</div>
          </div>
        </div>
      </div>
    </>
  );
}
